import dayjs from 'dayjs';

import { useState } from 'react';
import {
  findCTAType,
  getFormattedDueDateForAddTask,
  isTemporalEventId,
  transformChatSuggestedEventToCalendarEvent,
} from '../helpers';
import {
  ChatSuggestedEvent,
  DEFAULT_TASK_DURATION_MIN,
  eventsSnackBarAtom,
  getMealTypeFromName,
  TEMPORAL_EVENT_PREFIX,
  useAuthContext,
  useCalendarContext,
  useCircadianContext,
  useEventActions,
  useTaskActions,
  useUpdateMealPrediction,
  useUpdateSleepPrediction,
  useAddRoutine,
  useUpdateRoutine,
  useHideRoutine,
  FirestoreRoutineActivity,
} from '../data-access';
import { getSuggestedBlockKey } from '../data-access/helpers';
import { v4 as uuidv4 } from 'uuid';
import { useRecoilState } from 'recoil';
import { TodoAppFrom } from '@demind-inc/core';
import { ProjectFilterItem } from './types';
import { translateI18n } from '../i18n';

interface UseChatCTAHandlerParams {
  removeTemporalSuggestedEvent: (eventId: string) => void;
  setTemporalSuggestedEvents?: (
    events: ChatSuggestedEvent[] | ((prevEvents: ChatSuggestedEvent[]) => ChatSuggestedEvent[])
  ) => void;
}

export const useChatCTAHandler = ({
  removeTemporalSuggestedEvent,
  setTemporalSuggestedEvents,
}: UseChatCTAHandlerParams) => {
  const { mainCalendar, calendarPreferences } = useCalendarContext();
  const { user } = useAuthContext();
  const [_, setEventsSnackbar] = useRecoilState(eventsSnackBarAtom);
  const { createCalendarEvent, updateCalendarEvent, deleteCalendarEvent } = useEventActions();
  const { updateSleepPrediction } = useUpdateSleepPrediction();
  const { updateMealPrediction } = useUpdateMealPrediction();
  const { addTodoTask, deleteTask } = useTaskActions();
  const { addRoutine } = useAddRoutine();
  const { updateRoutine } = useUpdateRoutine();
  const { hideRoutine } = useHideRoutine();
  const [loadingEventId, setLoadingEventId] = useState<string | undefined>();

  const handleManageCTAEvent = async (event: ChatSuggestedEvent) => {
    const targetCalendarId = event.calendarId || mainCalendar?.calendarId;

    if (!targetCalendarId || !user?.userId) {
      return;
    }

    const shouldCreateNew = !event.before;
    const newEvent = transformChatSuggestedEventToCalendarEvent(event);

    if (shouldCreateNew) {
      const newEventId = (uuidv4() as string).replace(/-/g, '');

      await createCalendarEvent({
        calendarId: targetCalendarId,
        newEventOption: {
          ...newEvent,
          eventId: newEventId,
        },
      });

      setEventsSnackbar({ message: translateI18n('calendar.snackbar.eventCreated'), type: 'success' });

      return { ...newEvent, eventId: newEventId };
    } else {
      const updateEventId = isTemporalEventId(newEvent.eventId)
        ? event.autoScheduleFrom?.id
        : newEvent.eventId;
      if (!updateEventId) {
        setEventsSnackbar({ message: translateI18n('calendar.snackbar.couldNotUpdateEvent'), type: 'error' });
        return;
      }
      const { eventId: _previewOrStaleId, ...newEventFields } = newEvent;
      await updateCalendarEvent({
        calendarId: targetCalendarId,
        eventId: updateEventId,
        newEventOption: newEventFields,
      });
      setEventsSnackbar({ message: translateI18n('calendar.snackbar.eventUpdated'), type: 'success' });
      return { ...newEvent, eventId: updateEventId };
    }
  };

  const handleManageTodoOnCalendar = async (event: ChatSuggestedEvent) => {
    // Remove temporal suggested event before handling the CTA event
    if (event.taskFrom) {
      const temporalEventId = `${TEMPORAL_EVENT_PREFIX}${event.taskFrom.taskId}`;
      removeTemporalSuggestedEvent(temporalEventId);
    }

    await handleManageCTAEvent(event);
  };

  const handleManageTask = async (event: ChatSuggestedEvent, board: ProjectFilterItem) => {
    const taskId = uuidv4().replace(/-/g, '');
    const dueDate = getFormattedDueDateForAddTask('due', {
      datetime: event.endTime,
      date: event.date,
      timezone: dayjs.tz.guess(),
    });
    // A task card with no time slot must not land on the calendar.
    const shouldAddToCalendar = !!dueDate.dueDateTime?.datetime;

    await addTodoTask({
      taskId: event?.taskFrom?.taskId || taskId,
      boardId: board.todoTaskId,
      newTaskInfo: {
        taskId: event?.taskFrom?.taskId || taskId,
        name: event.name,
        appFrom: board.appFrom as TodoAppFrom,
        ...dueDate,
        duration: {
          unit: 'minute',
          amount: event.durationMin || DEFAULT_TASK_DURATION_MIN,
        },
        addedToCalendar: shouldAddToCalendar,
        completed: false,
        rawJson: {},
        updatedAt: new Date().toISOString(),
      },
      shouldReflectToEvent: shouldAddToCalendar,
    });
  };

  const handleManageSleep = async (event: ChatSuggestedEvent) => {
    if (!mainCalendar?.calendarId || !user?.metricId) {
      return;
    }
    const sleepTime = event.startTime;
    const wakeTime = event.endTime;
    const date = dayjs(wakeTime).format('YYYY-MM-DD');

    await updateSleepPrediction({
      metricId: user.metricId!,
      date,
      prediction: {
        sleepTime: dayjs(sleepTime).format('HH:mm'), // Support HH:mm format
        wakeTime: dayjs(wakeTime).format('HH:mm'), // Support HH:mm format
        description: event.description!,
      },
      shouldUpdateEvent: true,
      baseCalendarId: mainCalendar.calendarId,
      editedManually: false,
    });
  };

  const handleManageMeal = async (event: ChatSuggestedEvent) => {
    if (!mainCalendar?.calendarId || !user?.metricId) {
      return;
    }

    const date = dayjs(event.startTime).format('YYYY-MM-DD');
    const mealType = getMealTypeFromName(event.name);
    if (!mealType) {
      return;
    }

    const mc = event.mealContent;
    const foodItems = mc?.foodItems;
    const title = mc?.title;

    await updateMealPrediction({
      metricId: user.metricId!,
      date,
      prediction: {
        [mealType]: {
          startTime: dayjs(event.startTime).format('HH:mm'),
          endTime: dayjs(event.endTime).format('HH:mm'),
          description: event.description ?? '',
          ...(title ? { title } : {}),
          ...(foodItems?.length ? { foodItems } : {}),
        },
      },
      shouldUpdateEvent: true,
      baseCalendarId: mainCalendar.calendarId,
      editedManually: false,
    });
  };

  const handleManageRoutine = async (event: ChatSuggestedEvent) => {
    if (!user?.routineId) {
      return;
    }

    const isUpdate = !!event.before;
    const activityId = event.autoScheduleFrom?.id;

    // Convert ChatSuggestedEvent to FirestoreRoutineActivity
    const startTime = dayjs(event.startTime).format('HH:mm');
    const endTime = dayjs(event.endTime).format('HH:mm');
    const durationMin =
      event.durationMin || dayjs(event.endTime).diff(dayjs(event.startTime), 'minute');

    // Default recurring pattern (all days enabled)
    const defaultRecurring = {
      monday: { enabled: true },
      tuesday: { enabled: true },
      wednesday: { enabled: true },
      thursday: { enabled: true },
      friday: { enabled: true },
      saturday: { enabled: true },
      sunday: { enabled: true },
    };

    if (isUpdate && activityId && user.preferenceId) {
      // Update existing routine
      const routineActivityUpdate: Partial<FirestoreRoutineActivity> = {
        name: event.name,
        startTime: startTime,
        endTime: endTime,
        durationMin: durationMin,
        recurring: event.recurring ?? defaultRecurring,
      };

      await updateRoutine({
        routineId: user.routineId,
        activityId: activityId,
        preferenceId: user.preferenceId,
        routineActivity: routineActivityUpdate,
      });
    } else {
      // Create new routine
      const routineActivity: FirestoreRoutineActivity = {
        name: event.name,
        startTime: startTime,
        endTime: endTime,
        durationMin: durationMin,
        recurring: event.recurring ?? defaultRecurring,
      };

      await addRoutine({
        routineId: user.routineId,
        routineActivity,
      });
    }
  };

  const handleCTAEventMutation = async (event: ChatSuggestedEvent): Promise<boolean> => {
    setLoadingEventId(getSuggestedBlockKey(event));
    let removedTemporalEvent: ChatSuggestedEvent | undefined;

    // Hide temporal preview immediately to avoid showing a duplicate
    // while the optimistic calendar creation request is in-flight.
    if (event.action === 'setEvent') {
      removeTemporalSuggestedEvent(event.eventId);
      removedTemporalEvent = event;
    }

    try {
      const ctaType = findCTAType(event);
      if (ctaType === 'sleep') {
        await handleManageSleep(event);
      } else if (ctaType === 'meal') {
        await handleManageMeal(event);
      } else if (ctaType === 'todo') {
        await handleManageTodoOnCalendar(event);
      } else {
        await handleManageCTAEvent(event);
      }

      // Clear temporary preview event once CTA action succeeds.
      removeTemporalSuggestedEvent(event.eventId);

      // Keep backward compatibility for task-linked temporal ids.
      if (event.taskFrom?.taskId) {
        removeTemporalSuggestedEvent(`${TEMPORAL_EVENT_PREFIX}${event.taskFrom.taskId}`);
      }
      return true;
    } catch (error) {
      // Restore the preview when mutation fails so user can retry.
      if (removedTemporalEvent && setTemporalSuggestedEvents) {
        setTemporalSuggestedEvents((prevEvents) => {
          if (prevEvents.some((prevEvent) => prevEvent.eventId === removedTemporalEvent?.eventId)) {
            return prevEvents;
          }
          return [...prevEvents, removedTemporalEvent];
        });
      }
      console.error(error);
      return false;
    } finally {
      setLoadingEventId(undefined);
    }
  };

  const handleCTATask = async (
    event: ChatSuggestedEvent,
    targetBoard?: ProjectFilterItem
  ): Promise<boolean> => {
    if (!['setTask', 'deleteTask'].includes(event.action)) {
      return false;
    }

    setLoadingEventId(getSuggestedBlockKey(event));

    try {
      if (event.action === 'deleteTask' && event.taskFrom?.taskId && targetBoard?.todoTaskId) {
        await deleteTask({
          taskId: event.taskFrom?.taskId,
          boardId: targetBoard?.todoTaskId,
          shouldReflectToEvent: true,
          dueDateTime: {
            datetime: event.endTime,
            date: event.date,
            timezone: dayjs.tz.guess(),
          },
        });
      } else if (event.action === 'setTask') {
        await handleManageTask(event, targetBoard);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingEventId(undefined);
      return true;
    }
  };

  const handleDeleteEvent = async (event: ChatSuggestedEvent) => {
    const targetCalendarId = event.calendarId || mainCalendar?.calendarId;

    if (!targetCalendarId || !user?.userId) {
      return;
    }

    const targetEventId = isTemporalEventId(event.eventId)
      ? event.autoScheduleFrom?.id
      : event.eventId;
    if (!targetEventId) {
      return;
    }

    // Transform the event to calendar event format for deletion
    const calendarEvent = transformChatSuggestedEventToCalendarEvent(
      event,
      calendarPreferences?.unSyncToCalendar?.aiScheduler,
      calendarPreferences?.transparency?.aiScheduler
    );

    await deleteCalendarEvent({
      calendarId: targetCalendarId,
      event: {
        ...calendarEvent,
        eventId: targetEventId,
      },
      unSyncToCalendar: calendarPreferences?.unSyncToCalendar?.task,
    });

    setEventsSnackbar({ message: translateI18n('calendar.snackbar.eventDeleted'), type: 'success' });
  };

  const handleDeleteRoutine = async (event: ChatSuggestedEvent) => {
    if (!user?.routineId) {
      return;
    }

    const activityId = event.autoScheduleFrom?.id;
    if (!activityId) {
      return;
    }

    await hideRoutine({
      routineId: user.routineId,
      activityId: activityId,
    });

    setEventsSnackbar({ message: translateI18n('routine.snackbar.deleted'), type: 'success' });
  };

  const handleCTARoutine = async (event: ChatSuggestedEvent): Promise<boolean> => {
    if (event.action === 'deleteRoutine') {
      setLoadingEventId(getSuggestedBlockKey(event));
      try {
        await handleDeleteRoutine(event);
        return true;
      } catch (error) {
        console.error(error);
        return false;
      } finally {
        setLoadingEventId(undefined);
      }
    }

    if (event.action !== 'setRoutine') {
      return false;
    }
    setLoadingEventId(getSuggestedBlockKey(event));

    try {
      await handleManageRoutine(event);
      return true;
    } catch (error) {
      console.error(error);
      return false;
    } finally {
      setLoadingEventId(undefined);
    }
  };

  const handleCTAEvent = async (event: ChatSuggestedEvent): Promise<boolean> => {
    if (event.action === 'setRoutine' || event.action === 'deleteRoutine') {
      return handleCTARoutine(event);
    }

    if (event.action === 'deleteEvent') {
      setLoadingEventId(getSuggestedBlockKey(event));
      try {
        await handleDeleteEvent(event);
        return true;
      } catch (error) {
        console.error(error);
        return false;
      } finally {
        setLoadingEventId(undefined);
      }
    }

    if (event.action !== 'setEvent') {
      return;
    }
    return handleCTAEventMutation(event);
  };

  return { handleCTAEvent, handleCTATask, loadingEventId };
};
